import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { BgColorOptions, RatioOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NImageVideo } from '../../common/image-video/image-video.component';
import { NTextComponent } from '../../common/text/text.component';
import { Router } from '@angular/router';

export namespace NCmsHeroBanner {
    export interface IComponentData extends NICmsContentComponent.IData, NTextComponent.ITextContent, NImageVideo.IData {
        sideImage?: boolean;
        sideImageRight?: boolean;
        boxAlignement: string;
        ratio: string;
        link?: string;
        sideBgColor?: string;
        textComponentVariat?: 'default' | 'light';
    }

    export const getFields = () => {
        const fields: NICmsContentField.IField<any, any>[] = [
            <NICmsContentField.IToggleField>{
                key: 'sideImage',
                type: NICmsContentField.EFieldType.toggle,
                title: 'Side image',
                labels: { true: 'Side image', false: 'Full width image' },
                default: false
            },
            <NICmsContentField.IToggleField>{
                key: 'sideImageRight',
                type: NICmsContentField.EFieldType.toggle,
                title: 'Side image position',
                labels: { true: 'On right', false: 'On left' },
                default: false,
                canShow: data => !!data?.sideImage
            },
            <NICmsContentField.IChoiceField>{
                key: 'textComponentVariat',
                type: NICmsContentField.EFieldType.choice,
                title: 'Text component variant',
                options: [
                    { key: 'default', title: 'Default' },
                    { key: 'light', title: 'Light' }
                ],
                default: 'default',
                canShow: data => !data?.sideImage
            },
            <NICmsContentField.IChoiceField>{
                key: 'sideBgColor',
                type: NICmsContentField.EFieldType.choice,
                title: 'Side background color',
                previewMode: 'color',
                options: BgColorOptions,
                canShow: data => !!data?.sideImage
            },
            ...NTextComponent.getFields(true, true, false),
            <NICmsContentField.IChoiceField>{
                key: 'boxAlignement',
                type: NICmsContentField.EFieldType.choice,
                title: 'Box alignement',
                options: [
                    { key: 'left', title: 'Left / Mobile top' },
                    { key: 'center', title: 'Center' },
                    { key: 'right', title: 'Right / Mobile bottom' }
                ],
                description: 'Horizontal alignement of the box',
                default: 'left',
                required: true,
                canShow: data => !data?.sideImage
            },
            ...NImageVideo.CMS_FIELDS,
            {
                key: 'ratio',
                type: NICmsContentField.EFieldType.choice,
                title: 'Ratio',
                options: RatioOptions,
                required: true,
                default: 'ratio-3-2'
            },
            {
                key: 'link',
                type: NICmsContentField.EFieldType.text,
                title: 'Global link',
                default: '/'
            }
        ];

        return fields;
    };
}
@ICmsComponent<CmsHeroBannerComponent>({
    id: 'hero-banner',
    name: 'Hero Banner',
    tags: ['Hero', 'Banner', 'Homepage'],
    fields: NCmsHeroBanner.getFields()
})
@Component({
    selector: 'cms-hero-banner',
    templateUrl: './cms-hero-banner.html',
    styleUrls: ['cms-hero-banner.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsHeroBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component',
        watchDepth: 0
    })
    @Input()
    component!: NCmsHeroBanner.IComponentData;

    private router = inject(Router);

    protected navigateToLink(): void {
        if (this.component.link) {
            this.router.navigateByUrl(this.component.link);
        }
    }

    protected get boxPostition(): string {
        return this.client.sizeId > 1
            ? this.component.boxAlignement === 'right'
                ? 'top-50 right-0'
                : this.component.boxAlignement === 'center'
                  ? 'top-50 left-50 center _centerTransform'
                  : 'top-50'
            : this.component.boxAlignement === 'right'
              ? 'bottom-0'
              : this.component.boxAlignement === 'center'
                ? 'top-50'
                : 'top-0';
    }
}
